'use client';

import { forwardRef, Fragment, JSX, Ref, useEffect, useImperativeHandle, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Typography } from 'lighthouse';

import { InfiniteScrollListRef, ListProps } from './models';

export interface InfiniteScrollListType {
    <T>(props: ListProps<T>, ref?: Ref<InfiniteScrollListRef>): JSX.Element;
}

export const InfiniteScrollList = forwardRef(function InfiniteScrollList<T>(
    {
        infiniteScrollProps = {},
        defaultItems = [],
        renderRow,
        fetchData,
        containerProps = {},
        isLastPage = false,
        endMessage = (
            <Typography variant='subtitle2' className='mt-4 flex justify-center text-neutral-400'>
                «موارد بیشتری وجود ندارد‌»
            </Typography>
        )
    }: ListProps<T>,
    ref: Ref<InfiniteScrollListRef>
) {
    const [items, setItems] = useState(defaultItems);
    const [page, setPage] = useState<number>(0);
    const { style: infiniteScrollStyle, loader = null, ...restInfiniteScrollProps } = infiniteScrollProps;

    useEffect(() => {
        if (!items?.length) {
            setItems(defaultItems);
        }
    }, [defaultItems]);

    useImperativeHandle(
        ref,
        () => ({
            resetPaginate: () => {
                setPage(0);
            },
            refetchDefaultData: () => {
                fetchDefaultData();
            }
        }),
        [items, page]
    );

    const fetchMoreData = async () => {
        const nextPage = page + 1;
        const nextPageData = await fetchData(nextPage);

        if (Array.isArray(nextPageData)) {
            setItems((prev) => [...prev, ...nextPageData]);
            setPage(nextPage);
        }
    };

    const fetchDefaultData = async () => {
        setPage(0);
        const defaultData = await fetchData(0);

        if (Array.isArray(defaultData)) {
            setItems(defaultData);
        }
    };

    return (
        <InfiniteScroll
            dataLength={items.length}
            next={fetchMoreData}
            hasMore={!isLastPage}
            endMessage={endMessage}
            loader={loader}
            scrollableTarget={undefined}
            style={{ overflow: 'visible', ...infiniteScrollStyle }}
            {...restInfiniteScrollProps}>
            {!!items?.length && (
                <div {...containerProps}>
                    {items?.map((item, index) => (
                        <Fragment key={`public-list-item-${index}`}>{renderRow(item, index)}</Fragment>
                    ))}
                </div>
            )}
        </InfiniteScroll>
    );
}) as InfiniteScrollListType;
